import { CommonModule } from '@angular/common';
import { Component, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Constants } from '../../common';
import { CampaignService, CommonService, ContentService, PlaylistService } from '../../services';
import { SnackbarService } from '../../services';
import { IContentAddResponse, IPresignedURLResponse } from '../../models';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DynamicHeight } from '../../helper';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PlaylistDialogComponent } from './playlist-dialog';
import { IPlaylistAddResponse, IPlaylistContents } from '../../models/playlist.model';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkTreeModule, NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { forkJoin, Observable } from 'rxjs';

interface ICampaignPlaylistCard {
  playlist_id: number;
  playlist_name: string;
  created: string;
}

interface ICampaignScreenItem {
  screen_id: number;
  screen_name: string;
}

interface ICampaignScreenGroup {
  businessId: number | null;
  businessName: string;
  screens: ICampaignScreenItem[];
}

type AdManagerSection = 'media' | 'playlists' | 'campaign';

@Component({
  selector: 'app-ad-manager',
  imports: [
    DynamicHeight,
    CommonModule,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTreeModule,
    MatCheckboxModule,
    CdkTreeModule,
    MatSelectModule,
    MatListModule,
    MatDialogModule
  ],
  templateUrl: './ad-manager.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ad-manager.component.scss',
})
export class AdManagerComponent {
  mediaList: any[] = []
  playlistList: any[] = []
  campaignList: any[] = []
  screens: ICampaignScreenItem[] = []
  screenGroups: ICampaignScreenGroup[] = []
  visibleScreenGroups: ICampaignScreenGroup[] = []

  selectedPlaylistId: number | null = null
  selectedScreens: any[] = []

  playlists: ICampaignPlaylistCard[] = [];

  campaignMapping: any[] = [];
  campaignSearchTerm = '';
  activeSection: AdManagerSection = 'media';
  private readonly activeSectionStorageKey = 'ad-manager.active-section';

  campaignForm!: FormGroup

  displayedMediaColumns = ['name', 'type', 'size', 'orientation', 'created', 'action']
  displayedPlaylistColumns = ['name', 'created', 'action']
  displayedCampaignColumns = ['name', 'playlist', 'screens', 'created', 'action']

  treeControl = new NestedTreeControl<any>(node => node.children);
  dataSource = new MatTreeNestedDataSource<any>();
  screenSelection = new SelectionModel<any>(true);

  @ViewChild('confirmDialog') confirmDialog!: TemplateRef<any>;

  hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private contentService: ContentService,
    public commonService: CommonService,
    private playlistService: PlaylistService,
    private campaignService: CampaignService,
    private snackbarService: SnackbarService

  ) { }

  ngOnInit(): void {
    this.activeSection = this.loadActiveSection();
    this.loadMedia();
    this.loadPlaylists();
    this.loadBusinessScreens();
    this.loadCampaign();

  }

  openConfirmDialog(title: string, message: string, onConfirm: () => void) {
    this.dialog.open(this.confirmDialog, {
      data: { title, message, onConfirm }
    });
  }

  uploadMedia(event: any) {

    const files: FileList = event.target.files;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4'];

    for (let file of Array.from(files)) {

      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name} is not supported`);
        continue;
      }

      this.processFile(file);

    }

  }

  loadMedia() {
    this.commonService.showSpinner = true;
    this.contentService.getAllContents().subscribe({

      next: (response) => {

        const results = response?.data?.results || [];

        this.mediaList = results.map(item => {

          let orientation = 'None';

          if (item.resolution) {

            const [width, height] = item.resolution.split('x').map(Number);

            if (width > height) orientation = 'Landscape';
            else if (height > width) orientation = 'Portrait';

          }

          return {
            id: item.id,
            name: item.title,
            type: item.file_format,
            size: item.file_size,
            resolution: item.resolution,
            orientation: orientation,
            duration: item.file_duration,
            created: item.created_at
          };

        });
        this.commonService.showSpinner = false;
      },

      error: err => {
        console.error('Failed to load media', err);
        this.snackbarService.open('Failed to load media');
        this.commonService.showSpinner = false;
      }

    });

  }

  loadPlaylists() {

    this.playlistService.getAllPlaylist().subscribe({

      next: (res: any) => {

        const results = res?.data?.results || [];

        this.playlistList = results.map((item: any) => ({
          id: item.id,
          name: item.name,
          // media_count: item.zones?.[0]?.contents?.length || 0,
          created: item.created_at
        }));

        this.buildCampaignUI();

      },

      error: err => {
        console.error('Playlist load failed', err);
        this.snackbarService.open('Failed to load playlists');
      }

    });

  }

  loadCampaign() {

    this.campaignService.getCampaign().subscribe({

      next: (res: any) => {

        const campaign = res || [];

        this.campaignMapping = campaign.map((p: any) => ({
          playlist_id: p.playlist_id,
          playlist_name: p.playlist_name,
          screens: p.screens || []
        }));

        this.buildCampaignUI();

      },

      error: err => {
        console.error('Campaign load failed', err);
        this.snackbarService.open('Failed to load campaign mappings');
      }

    });

  }

  buildCampaignUI() {

    this.playlists = this.playlistList.map(p => ({
      playlist_id: p.id,
      playlist_name: p.name,
      created: p.created
    }));

    if (!this.playlists.length) {
      this.selectedPlaylistId = null;
      return;
    }

    const selectedExists = this.playlists.some((playlist) => playlist.playlist_id === this.selectedPlaylistId);
    if (!selectedExists) {
      this.selectedPlaylistId = this.playlists[0].playlist_id;
    }

  }

  selectExistingScreens(screenIds: number[]) {

    const nodes = this.treeControl.dataNodes;

    const allNodes = this.treeControl.getDescendants(nodes[0] ?? []);

    const leafNodes = nodes.flatMap(node =>
      this.treeControl.getDescendants(node)
    );

    leafNodes.forEach(node => {

      if (node.type === 'screen' && screenIds.includes(node.screenId)) {

        this.screenSelection.select(node);

      }

    });

    this.updateSelectedScreens();

  }

  updateSelectedScreens() {

    this.selectedScreens = this.screenSelection.selected
      .filter(node => node.type === 'screen')
      .map(node => ({
        id: node.screenId,
        name: node.name
      }));

  }



  loadBusinessScreens() {

    this.campaignService.getAllBusinessScreens().subscribe({

      next: (res: any[]) => {

        this.screenGroups = (res ?? [])
          .map((business: any) => ({
            businessId: this.asNullableNumber(business.business_id),
            businessName: this.asText(business.business_name, 'Unassigned'),
            screens: (business.screens ?? [])
              .map((screen: any) => ({
                screen_id: screen.screen_id,
                screen_name: this.asText(screen.screen_name, `Screen ${screen.screen_id}`)
              }))
              .sort((left: ICampaignScreenItem, right: ICampaignScreenItem) =>
                left.screen_name.localeCompare(right.screen_name)
              )
          }))
          .sort((left: ICampaignScreenGroup, right: ICampaignScreenGroup) =>
            left.businessName.localeCompare(right.businessName)
          );

        this.screens = this.screenGroups.flatMap((group) => group.screens);
        this.refreshVisibleScreenGroups();

      },

      error: err => console.error(err)

    });

  }

  setActiveSection(section: AdManagerSection) {
    this.activeSection = section;
    this.saveActiveSection(section);
  }

  selectPlaylist(p: any) {

    this.selectedPlaylistId = p.playlist_id

  }

  onCampaignSearch(event: Event) {
    this.campaignSearchTerm = (event.target as HTMLInputElement).value;
    this.refreshVisibleScreenGroups();
  }

  onScreenToggle(node: any) {

    this.screenSelection.toggle(node)

    this.selectedScreens = this.screenSelection.selected
      .filter(n => n.type === 'screen')
      .map(n => ({ id: n.screenId, name: n.name }))

  }


  processFile(file: File) {

    if (file.type.startsWith('image')) {

      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e: any) => img.src = e.target.result;

      img.onload = () => {

        const resolution = `${img.width}x${img.height}`;
        const orientation = this.getOrientation(img.width, img.height);

        this.startUpload(file, resolution, orientation);

      };

      reader.readAsDataURL(file);
    }

    else if (file.type === 'video/mp4') {

      const video = document.createElement('video');

      video.preload = 'metadata';

      video.onloadedmetadata = () => {

        const resolution = `${video.videoWidth}x${video.videoHeight}`;
        const orientation = this.getOrientation(video.videoWidth, video.videoHeight);

        this.startUpload(file, resolution, orientation);

      };

      video.src = URL.createObjectURL(file);

    }

  }

  startUpload(file: File, resolution: string, orientation: string) {
    this.commonService.showSpinner = true;

    this.contentService
      .getPresignedURLForS3(file.name, file.type, file.size)
      .subscribe({

        next: (presigned: IPresignedURLResponse) => {

          const presignedUrl = presigned.url;

          this.contentService
            .uploadFileToS3(presignedUrl, file)
            .subscribe({

              next: () => {

                const uploadedFileUrl = presignedUrl.split('?')[0];

                const formData = new FormData();

                formData.append('title', file.name);
                formData.append('file_format', file.name.split('.').pop() || '');
                formData.append('file_size', file.size.toString());
                formData.append('resolution', resolution);
                formData.append('file_upload', uploadedFileUrl);
                formData.append('thumbnail', '0');
                formData.append('category', 'Admin ads');
                formData.append('source', 'ZignFlix-Admin');
                formData.append('orientation_type', orientation.toLowerCase());

                this.contentService.addContents(formData).subscribe({

                  next: (response: IContentAddResponse) => {

                    this.commonService.showSpinner = false;
                    this.loadMedia();
                    this.snackbarService.open(response?.message || 'Media added successfully');

                    const content = response.data;

                    this.mediaList.push({
                      name: content.title,
                      type: content.file_format,
                      thumbnail: content.thumbnail,
                      size: content.file_size,
                      resolution: content.resolution,
                      orientation: content.orientation_type,
                      created: content.created_at ? new Date(content.created_at) : new Date()
                    });

                  },

                  error: err => {
                    this.commonService.showSpinner = false;
                    console.error('Metadata save failed', err);
                    this.snackbarService.open(this.getErrorMessage(err, 'Failed to save media'));
                  }

                });

              },

              error: err => {
                this.commonService.showSpinner = false;
                console.error('S3 upload failed', err);
                this.snackbarService.open(this.getErrorMessage(err, 'Failed to upload media'));
              }

            });

        },

        error: err => {
          this.commonService.showSpinner = false;
          console.error('Presigned URL failed', err);
          this.snackbarService.open(this.getErrorMessage(err, 'Failed to prepare media upload'));
        }

      });

  }

  getOrientation(width: number, height: number): string {

    if (width > height) return 'Landscape';
    if (height > width) return 'Portrait';
    return 'None';

  }

  deleteMedia(element: any) {
    this.openConfirmDialog(
      'Delete Media',
      `Delete "${element.name}"? This action cannot be undone.`,
      () => {
        this.dialog.closeAll();

        this.commonService.showSpinner = true;

        this.contentService.deleteContentsById(element.id)
          .subscribe({
            next: (response) => {
              this.loadMedia();
              this.snackbarService.open(response?.message || 'Media deleted successfully');
            },
            error: err => {
              this.commonService.showSpinner = false;
              console.error('Delete failed', err);
              this.snackbarService.open(this.getErrorMessage(err, 'Failed to delete media'));
            }
          });
      }
    );
  }

  openCreatePlaylist() {

    const dialogRef = this.dialog.open(PlaylistDialogComponent, {
      minWidth: '150vh',
      data: {
        mediaList: this.mediaList
      }
    });

    dialogRef.afterClosed().subscribe(res => {

      if (res) {
        this.playlistService.addPlaylist(res).subscribe({
          next: (response: any) => {
            this.loadPlaylists();
            this.snackbarService.open(response?.message || 'Playlist created successfully');
          },
          error: err => {
            console.error('Playlist creation failed', err);
            this.snackbarService.open(this.getErrorMessage(err, 'Failed to create playlist'));
          }
        });
      }

    });

  }

  openEditPlaylist(id: number) {

    this.commonService.showSpinner = true;

    this.playlistService.getPlaylistById(id).subscribe({

      next: (res: IPlaylistContents) => {

        this.commonService.showSpinner = false;

        const playlist = res;

        const dialogRef = this.dialog.open(PlaylistDialogComponent, {
          minWidth: '150vh',
          data: {
            mediaList: this.mediaList,
            playlist: playlist
          }
        });

        dialogRef.afterClosed().subscribe(payload => {

          if (!payload) return;

          this.playlistService.updatePlaylist(id, payload).subscribe({

            next: (response: any) => {
              this.loadPlaylists();
              this.snackbarService.open(response?.message || 'Playlist updated successfully');
            },

            error: err => {
              console.error(err);
              this.snackbarService.open(this.getErrorMessage(err, 'Failed to update playlist'));
            }

          });

        });

      },

      error: err => {
        this.commonService.showSpinner = false;
        console.error(err);
        this.snackbarService.open(this.getErrorMessage(err, 'Failed to load playlist'));
      }

    });

  }

  deletePlaylist(index: number) {
    const playlist = this.playlistList[index];

    if (!playlist?.id) {
      this.playlistList.splice(index, 1);
      return;
    }

    this.openConfirmDialog(
      'Delete Playlist',
      `Delete playlist "${playlist.name}"? This action cannot be undone.`,
      () => {
        this.dialog.closeAll();

        this.commonService.showSpinner = true;

        this.playlistService.deletePlaylistById(playlist.id).subscribe({
          next: (response: any) => {
            this.playlistList.splice(index, 1);
            this.commonService.showSpinner = false;
            this.loadPlaylists();
            this.snackbarService.open(response?.message || 'Playlist deleted successfully');
          },
          error: err => {
            this.commonService.showSpinner = false;
            console.error(err);
            this.snackbarService.open(this.getErrorMessage(err, 'Failed to delete playlist'));
          }
        });
      }
    );
  }


  deleteCampaign(index: number) {

    const campaign = this.campaignList[index];

    const confirmDelete = confirm(`Delete campaign "${campaign.name}"?`);

    if (!confirmDelete) return;

    this.commonService.showSpinner = true;

    // this.campaignService.deleteCampaignById(campaign.id).subscribe({

    //   next: () => {

    //     this.commonService.showSpinner = false;
    //     this.loadCampaigns();

    //   },

    //   error: err => {

    //     this.commonService.showSpinner = false;
    //     console.error(err);

    //   }

    // });

  }

  toggleScreen(screen: any, playlist: any, event: any) {

    const checked = event.target.checked;

    let mapping = this.campaignMapping.find(
      p => p.playlist_id === playlist.playlist_id
    );

    if (!mapping) {
      mapping = {
        playlist_id: playlist.playlist_id,
        playlist_name: playlist.playlist_name,
        screens: []
      };

      this.campaignMapping.push(mapping);
    }

    if (checked) {

      // Remove screen from other playlists (only one playlist per screen)
      this.campaignMapping.forEach(p => {
        if (p.playlist_id !== playlist.playlist_id) {
          p.screens = p.screens.filter(
            (s: any) => s.screen_id !== screen.screen_id
          );
        }
      });

      // Add to current playlist
      if (!mapping.screens.some((s: any) => s.screen_id === screen.screen_id)) {
        mapping.screens.push({
          screen_id: screen.screen_id,
          screen_name: screen.screen_name
        });
      }

    } else {

      // Remove from this playlist
      mapping.screens = mapping.screens.filter(
        (s: any) => s.screen_id !== screen.screen_id
      );

    }

  }

  selectAllScreens(playlist: any) {

    // Remove all screens from all playlists
    this.campaignMapping.forEach(p => {
      p.screens = p.screens.filter(
        (s: any) => !this.screens.some(sc => sc.screen_id === s.screen_id)
      );
    });

    // Find playlist mapping
    let mapping = this.campaignMapping.find(
      p => p.playlist_id === playlist.playlist_id
    );

    if (!mapping) {

      mapping = {
        playlist_id: playlist.playlist_id,
        playlist_name: playlist.playlist_name,
        screens: []
      };

      this.campaignMapping.push(mapping);

    }

    // Assign ALL screens
    mapping.screens = this.screens.map(screen => ({
      screen_id: screen.screen_id,
      screen_name: screen.screen_name
    }));

  }

  deselectAllScreens(playlist: any) {

    const mapping = this.campaignMapping.find(
      p => p.playlist_id === playlist.playlist_id
    );

    if (mapping) {
      mapping.screens = [];
    }

  }

  clearScreens(playlist: any) {

    const mapping = this.campaignMapping.find(
      p => p.playlist_id === playlist.playlist_id
    )

    if (mapping) {
      mapping.screens = []
    }

  }
  isScreenAssigned(screenId: number, playlistId: number) {

    const playlist = this.campaignMapping.find(
      p => p.playlist_id === playlistId
    )

    if (!playlist) return false;

    return playlist.screens.some(
      (s: any) => s.screen_id === screenId
    )

  }



  saveCampaign() {

    const requestMap: Record<string, Observable<IPlaylistAddResponse>> = {};

    this.campaignMapping.forEach((p, index) => {

      const payload: any = {
        composition_id: p.playlist_id,
        screen_ids: p.screens.map((s: any) => s.screen_id) || []
      };

      requestMap['req' + index] =
        this.campaignService.saveCampaign(payload);

    });

    if (!Object.keys(requestMap).length) return;

    this.commonService.showSpinner = true;

    forkJoin(requestMap).subscribe({

      next: (responses) => {

        this.commonService.showSpinner = false;
        console.log('Campaign saved successfully');
        const firstMessage = Object.values(responses)
          .map((response) => response?.message?.trim())
          .find((message): message is string => !!message);

        this.snackbarService.open(firstMessage || 'Campaign saved successfully');

      },

      error: err => {

        this.commonService.showSpinner = false;
        console.error('Campaign save failed', err);
        this.snackbarService.open(this.getErrorMessage(err, 'Failed to save campaign'));

      }

    });

  }

  private asNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private asText(value: unknown, fallback: string): string {
    return this.asNullableText(value) ?? fallback;
  }

  private asNullableText(value: unknown): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }

    return null;
  }

  private getErrorMessage(error: unknown, fallback: string = Constants.NOTIFY_MESSAGES.error): string {
    const errorObject = error as { error?: { message?: string }; message?: string };
    return errorObject?.error?.message || errorObject?.message || fallback;
  }

  get selectedPlaylist() {
    return this.playlists.find((playlist) => playlist.playlist_id === this.selectedPlaylistId) ?? null;
  }

  getPlaylistScreenCount(playlistId: number): number {
    const mapping = this.campaignMapping.find((playlist: any) => playlist.playlist_id === playlistId);
    return mapping?.screens?.length ?? 0;
  }

  getPlaylistScreens(playlistId: number): Array<{ id: number; name: string }> {
    const mapping = this.campaignMapping.find((playlist: any) => playlist.playlist_id === playlistId);
    return (mapping?.screens ?? []).map((screen: any) => ({
      id: screen.screen_id,
      name: screen.screen_name
    }));
  }

  getScreenAssignedPlaylist(screenId: number): { playlistId: number; playlistName: string } | null {
    const playlist = this.campaignMapping.find((mapping: any) =>
      (mapping.screens ?? []).some((screen: any) => screen.screen_id === screenId)
    );

    if (!playlist) {
      return null;
    }

    return {
      playlistId: playlist.playlist_id,
      playlistName: playlist.playlist_name
    };
  }

  getScreenAssignmentState(screenId: number, playlistId: number): 'available' | 'selected' | 'owned' {
    const assignment = this.getScreenAssignedPlaylist(screenId);
    if (!assignment) {
      return 'available';
    }

    return assignment.playlistId === playlistId ? 'selected' : 'owned';
  }

  getScreenAssignmentLabel(screenId: number, playlistId: number): string {
    const assignment = this.getScreenAssignedPlaylist(screenId);
    if (!assignment) {
      return 'Available';
    }

    return assignment.playlistId === playlistId ? 'In playlist' : `In ${assignment.playlistName}`;
  }

  getGroupAssignedCount(group: ICampaignScreenGroup): number {
    const activePlaylist = this.selectedPlaylist;
    if (!activePlaylist) {
      return 0;
    }

    return group.screens.filter((screen) => this.isScreenAssigned(screen.screen_id, activePlaylist.playlist_id)).length;
  }

  refreshVisibleScreenGroups() {
    const searchValue = this.campaignSearchTerm.trim().toLowerCase();

    if (!searchValue) {
      this.visibleScreenGroups = this.screenGroups;
      return;
    }

    this.visibleScreenGroups = this.screenGroups
      .map((group) => {
        const groupMatches = group.businessName.toLowerCase().includes(searchValue);
        const screens = groupMatches
          ? group.screens
          : group.screens.filter((screen) => screen.screen_name.toLowerCase().includes(searchValue));

        return {
          ...group,
          screens
        };
      })
      .filter((group) => group.businessName.toLowerCase().includes(searchValue) || group.screens.length > 0);
  }

  private loadActiveSection(): AdManagerSection {
    if (typeof window === 'undefined') {
      return 'media';
    }

    const stored = window.localStorage.getItem(this.activeSectionStorageKey);
    if (stored === 'media' || stored === 'playlists' || stored === 'campaign') {
      return stored;
    }

    return 'media';
  }

  private saveActiveSection(section: AdManagerSection) {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.activeSectionStorageKey, section);
  }

}
